import colors from "utils/colors";
import React, { useEffect, useState, useCallback } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { Input, InputProps } from "@chakra-ui/react";
import { Box, List, ListItem, ListIcon, Flex } from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";

interface LocationAutocompleteProps extends InputProps {
	onChange: (location: any) => void;
	defaultValue?: string;
	disabled?: boolean;
}

const LocationAutocomplete = React.memo(
	({
		onChange,
		defaultValue,
		disabled,
		...props
	}: LocationAutocompleteProps) => {
		const [inputValue, setInputValue] = useState(defaultValue || "");

		const [selectedPlace, setSelectedPlace] = useState<any>(null);
		const [isGoogleSelection, setIsGoogleSelection] = useState(false);
		const [isInputFocused, setIsInputFocused] = useState(false);
		const [showSuggestions, setShowSuggestions] = useState(false);

		const { placesService, placePredictions, getPlacePredictions } =
			usePlacesService({
				apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
			});

		const formatPlace = useCallback(
			(place: any) => {
				if (
					!place ||
					!place.formatted_address ||
					!place.geometry ||
					!place.geometry.location
				) {
					console.error("Invalid place object:", place);
					return;
				}
				let formatted = place.formatted_address;
				if (formatted.endsWith(", USA")) {
					formatted = formatted.slice(0, -5);
				}
				const components = place.address_components || [];
				const city = (
					components.find((comp: any) => comp.types.includes("locality")) ||
					components.find((comp: any) =>
						comp.types.includes("administrative_area_level_2")
					) ||
					components.find((comp: any) => comp.types.includes("neighborhood")) ||
					{}
				).short_name;

				const state = (
					components.find(
						(comp: any) =>
							comp.types.includes("administrative_area_level_1") &&
							comp.types.includes("political")
					) || {}
				).short_name;

				const country = (
					components.find(
						(comp: any) =>
							comp.types.includes("country") && comp.types.includes("political")
					) || {}
				).short_name;

				const zip = (
					components.find((comp: any) => comp.types.includes("postal_code")) ||
					{}
				).short_name;

				const county = (
					components.find((comp: any) =>
						comp.types.includes("administrative_area_level_2")
					) || {}
				).long_name;

				const latitude = place.geometry.location.lat();
				const longitude = place.geometry.location.lng();

				onChange({
					place_id: place.place_id,
					name: place.name,
					formatted: place.formatted_address,
					city,
					state,
					country,
					zip,
					county,
					latitude,
					longitude,
				});
			},
			[onChange]
		);

		const handlePlaceSelected = (place: any) => {
			if (place.description !== inputValue) {
				setSelectedPlace(place);
				setInputValue(place.description);
				setIsGoogleSelection(true);
				setShowSuggestions(false);
			}
		};

		useEffect(() => {
			if (!isInputFocused && isGoogleSelection && selectedPlace) {
				placesService?.getDetails(
					{
						placeId: selectedPlace.place_id,
					},
					(placeDetails) => {
						formatPlace(placeDetails);
					}
				);
			}
		}, [
			formatPlace,
			isGoogleSelection,
			isInputFocused,
			placesService,
			selectedPlace,
		]);

		function handleOnChange(evt: React.ChangeEvent<HTMLInputElement>) {
			getPlacePredictions({ input: evt.target.value });
			setInputValue(evt.target.value);
		}

		return (
			<>
				<Input
					placeholder="Enter a location"
					data-cy="location-autocomplete"
					value={inputValue}
					variant="primary"
					isDisabled={disabled}
					onChange={handleOnChange}
					onFocus={() => {
						setIsInputFocused(true);
						setShowSuggestions(true);
					}}
					onBlur={() => {
						setIsInputFocused(false);
						setTimeout(() => {
							setShowSuggestions(false);
						}, 200);
					}}
					{...props}
				/>
				{showSuggestions && placePredictions && inputValue && (
					<Box position="absolute" zIndex="1" background={"white"} width="100%">
						<Box
							border="1px solid"
              borderTop={0}
							borderColor={colors.GRAY_50}
						>
							<List>
								{placePredictions.map((item, index) => (
									<ListItem
										p={2}
										key={item.place_id}
										onClick={() => handlePlaceSelected(item)}
										cursor="pointer"
										_hover={{
											background: colors.GRAY_50,
										}}
										borderBottom={
											index < placePredictions.length - 1
												? `1px solid ${colors.GRAY_50}`
												: undefined
										}
									>
										<Flex alignItems="center">
											<ListIcon as={MdLocationOn} color={colors.SECONDARY} />
											{item.description}
										</Flex>
									</ListItem>
								))}
							</List>
						</Box>
					</Box>
				)}
			</>
		);
	}
);

const LocationAutocompleteHoc = (props: LocationAutocompleteProps) => {
	if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
		return <p style={{ color: "red" }}>Error: No google maps API key set</p>;
	}
	return <LocationAutocomplete {...props} />;
};
export default LocationAutocompleteHoc;
